/*
 * Copyright (c) "Neo4j"
 * Neo4j Sweden AB [http://neo4j.com]
 *
 * This file is part of Neo4j.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
import { toGraphQLTypeDefs } from '@neo4j/introspector';
import { APP_SCOPE } from '@nx/constants';
import { createLogger } from '@nx/logger/legacy';
import type { Driver, Session } from 'neo4j-driver';
import neo4j from 'neo4j-driver';

interface LoginOptions {
  username: string;
  password: string;
  url: string;
}

const logger = createLogger(APP_SCOPE.dataApi);

export function useTypeDefinitionIntrospector() {
  const login = async (options: LoginOptions) => {
    const auth = neo4j.auth.basic(options.username, options.password);
    const driver = neo4j.driver(options.url, auth);
    await driver.verifyConnectivity();
    return driver;
  };

  const getSessionFactory = (driver: Driver) => (): Session => {
    return driver.session({
      defaultAccessMode: neo4j.session.READ,
      database: 'neo4j',
    });
  };

  const getTypeDefinitions = async (
    options: LoginOptions,
  ): Promise<{
    typeDefs: string;
    error?: string;
  }> => {
    try {
      const driver = await login(options);
      const sessionFactory = getSessionFactory(driver);
      const typeDefs = await toGraphQLTypeDefs(sessionFactory);
      return { typeDefs };
    } catch (error) {
      logger.error('Error retrieving type definitions:', error);
      return {
        typeDefs: '',
        error: 'Failed to retrieve type definitions. Please check your selected instance and credentials.',
      };
    }
  };

  return {
    getTypeDefinitions,
  };
}
